import { Suspense, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import ErrorBoundary from '~/App/components/ErrorBoundary';
import Header from '~/App/components/Header';
import Loading from '~/App/components/Loading';
import Wrapper from '~/App/components/Wrapper';
import RoleForm from '../../components/RoleForm';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import api from '../../api';
import message from '~/utils/message';
import { RoleFormValues } from '../../types';

const EditRoleContent = ({ id }: { id: string }) => {
  const { data } = useSuspenseQuery({
    queryKey: ['fetchRole', id],
    queryFn: () => api.getRole(id),
    staleTime: 0,
    gcTime: 0,
  });
  const navigate = useNavigate();

  const { isPending, mutate } = useMutation({
    mutationFn: (values: RoleFormValues) => api.updateRole(id, values),
    retry: false,
    onSuccess: () => {
      message.success('Success edit role');
      navigate('..');
    },
    onError: () => {
      message.error('Fail to edit role');
    },
  });

  const initialFormValues: RoleFormValues = useMemo(() => {
    const { permissions, ...rest } = data;
    return {
      ...rest,
      permissionIds: permissions.map(({ permissionId }) => permissionId),
    };
  }, [data]);

  return (
    <RoleForm
      initialValues={initialFormValues}
      loading={isPending}
      onSubmit={mutate}
    />
  );
};

const EditRole = () => {
  const { id } = useParams();
  if (!id) return <Navigate to="/role" />;
  return (
    <Wrapper>
      <Header title="Edit Role" />
      <ErrorBoundary>
        <Suspense fallback={<Loading className="h-[400px]" />}>
          <EditRoleContent id={id} />
        </Suspense>
      </ErrorBoundary>
    </Wrapper>
  );
};

export default EditRole;
